
// ----------------------------------------------------------------------------
// Component: Tab
// ----------------------------------------------------------------------------
//
// TAB component with desktop tab functionality.
//
// Requires the following structure:
// .js-tab__item
//     .js-tab__button
//     .js-tab__content
//
// When the button is clicked, 'isOpen' class is added/removed for all three
// elements.
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Scripts
import Swiper, { Navigation, A11y, Keyboard } from 'swiper';
import 'snippets/_containers/css-grid/css-grid';
import 'snippets/icon/icon';

// Styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/a11y';
import 'swiper/css/keyboard';
import 'sections/tabs/tabs.js';
import 'sections/tabs/_tabs.scss';

import './_size-guide.scss';

// -------------------------------------
// Variables
// -------------------------------------

const SIZE_GUIDE_SECTION             = '.size-guide';

const TAB_ACCORDION                  = '.js-tabs--accordion';
const TAB_ITEM_ACCORDION             = '.js-tab__item--accordion';
const TAB_TITLE_ACCORDION            = '.js-tab__title--accordion';
const TAB_BUTTON_ACCORDION           = '.js-tab__button--accordion';
const TAB_CONTENT_ACCORDION          = '.js-tab__content--accordion';

const TAB_HORIZONTAL                 = '.js-tabs--horizontal';
const TAB_BUTTON_HORIZONTAL          = '.js-tab__button--horizontal';
const TAB_CONTENT_HORIZONTAL         = '.js-tab__content--horizontal';

const OPEN_CLASS                     = 'is-open';
const OPEN_CLASS_SELECTOR            = '.is-open';
const SWIPER_CLASS                   = 'swiper--tabs';
const SWIPER_CLASS_SELECTOR          = '.swiper--tabs';
const SWIPER_NAV_PREV_CLASS          = 'swiper-button-prev--tabs';
const SWIPER_NAV_PREV_CLASS_SELECTOR = '.swiper-button-prev--tabs';
const SWIPER_NAV_NEXT_CLASS          = 'swiper-button-next--tabs';
const SWIPER_NAV_NEXT_CLASS_SELECTOR = '.swiper-button-next--tabs';
const HORIZONTAL_KEYS                = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
const ACCORDION_KEYS                 = ['ArrowUp', 'ArrowDown', 'Home', 'End'];


// -------------------------------------
// Helper functions
// -------------------------------------

// Initialize horizontal tabs
function initHorizontalTabs(buttons, content) {
    buttons.forEach(function(button) {
        button.addEventListener('click', function(event) {
            event.preventDefault();
            updateHorizontalTabs(event.target, buttons, content);
        });

        button.addEventListener('keydown', function(event) {
            handleKeyboard('horizontal', event, buttons, content);
        });
    });
}

// Update the active horizontal tab
function updateHorizontalTabs(button, allButtons, allContent) {
    const content = document.getElementById(button.getAttribute('aria-controls'));

    if (!button.classList.contains(OPEN_CLASS)) {
        // Close all content
        updateAttributeForElements(allButtons, 'aria-selected', 'false');
        updateAttributeForElements(allButtons, 'tabindex', '-1');
        removeClassFromElements(allButtons, OPEN_CLASS);
        removeClassFromElements(allContent, OPEN_CLASS);

        // Open current content
        openHorizontalTab(button, content);
    }
}

// Open a horizontal tab
function openHorizontalTab(button, content) {
    button.classList.add(OPEN_CLASS);
    button.setAttribute('aria-selected', 'true');
    button.setAttribute('tabindex', '0');
    content.classList.add(OPEN_CLASS);
}

// Initialize accordion tabs
function initAccordionTabs(buttons) {
    buttons.forEach(function(button) {
        button.addEventListener('click', function(event) {
            event.preventDefault();
            updateAccordionTabs(event.target);
        });

        button.addEventListener('keydown', function(event) {
            handleKeyboard('accordion', event, buttons, null);
        });
    });
}

// Update the active accordion tab
function updateAccordionTabs(button) {
    const tab                   =  generateTab(button);
    const tabs                  = tab.$item.parentNode;
    const openClassElems        = tabs.querySelectorAll(OPEN_CLASS_SELECTOR);
    const accordionContentItems = tabs.querySelectorAll(TAB_CONTENT_ACCORDION);
    const accordionButtons      = tabs.querySelectorAll(TAB_BUTTON_ACCORDION);

    // Close all open tabs
    removeClassFromElements(openClassElems, OPEN_CLASS);
    updateStyleForElements(accordionContentItems, 'overflow', 'hidden');
    updateAttributeForElements(accordionButtons, 'aria-expanded', 'false');

    // Set tabindex for all content links
    accordionContentItems.forEach(function(contentItem) {
        let itemLinks = contentItem.querySelectorAll('a');

        updateAttributeForElements(itemLinks, 'tabindex', '-1');
    });

    // If the curent tab isn't already open, open it
    if (tab.$isOpening) {
        openAccordionTab(tab);
    }
}

// Open an accordion tab
function openAccordionTab(tab) {
    tab.$item.classList.add(OPEN_CLASS);
    tab.$button.classList.add(OPEN_CLASS);
    tab.$content.classList.add(OPEN_CLASS);
    tab.$button.setAttribute('aria-expanded', 'true');
    tab.$content.setAttribute('hidden', false);

    let contentItemLinks = tab.$content.querySelectorAll('a');

    updateAttributeForElements(contentItemLinks, 'tabindex', null);

    // This allows select boxes to flow out after it's opened
    setTimeout(function() {
        tab.$content.style.overflow = 'visible';
    }, 200);
}

// Builds a "tab" object
function generateTab(button) {
    const tab =  {
        $container : button.closest(TAB_ACCORDION),
        $item      : button.closest(TAB_ITEM_ACCORDION),
        $title     : button.closest(TAB_TITLE_ACCORDION),
        $button    : button,
        $content   : button.closest(TAB_ITEM_ACCORDION).querySelector(TAB_CONTENT_ACCORDION),
        $isOpening : !button.classList.contains(OPEN_CLASS)
    };

    return tab;
}

// Handle keyboard events for buttons
function handleKeyboard(source, event, buttons, content) {
    const $this = event.target;
    const key = event.key;
    const sourceKeys = source === 'horizontal' ? HORIZONTAL_KEYS : ACCORDION_KEYS;

    if (sourceKeys.includes(key)) {
        event.preventDefault();

        let index;
        const length = buttons.length;
        let newIndex;

        for (let i = 0; i < length; i++){
            if (buttons[i] === $this) {
                index = i;
                break;
            }
        }

        switch(key) {
            case 'ArrowUp':
                newIndex = (index + length - 1) % length;
                buttons[newIndex].focus();
                break;
            case 'ArrowDown':
                newIndex = (index + length + 1) % length;
                buttons[newIndex].focus();
                break;
            case 'ArrowLeft':
                newIndex = (index + length - 1) % length;
                buttons[newIndex].focus();
                updateHorizontalTabs(buttons[newIndex], buttons, content);
                break;
            case 'ArrowRight':
                newIndex = (index + length + 1) % length;
                buttons[newIndex].focus();
                updateHorizontalTabs(buttons[newIndex], buttons, content);
                break;
            case 'Home':
                buttons[0].focus();

                if (source === 'horizontal') {
                    updateHorizontalTabs(buttons[0], buttons, content);
                }

                break;
            case 'End':
                newIndex = (length - 1) % length;
                buttons[newIndex].focus();

                if (source === 'horizontal') {
                    updateHorizontalTabs(buttons[newIndex], buttons, content);
                }

                break;
        }
    }
}

// Update a given attribute for a list of elements
function updateAttributeForElements(elems, attribute, value) {
    elems?.forEach(function(elem) {
        elem.setAttribute(attribute, value);
    });
}

// Remove a given class from a list of elements
function removeClassFromElements(elems, className) {
    elems.forEach(function(elem) {
        elem.classList.remove(className);
    });
}

// Update a given style for a list of elements
function updateStyleForElements(elems, style, value) {
    elems.forEach(function(elem) {
        elem.style[style] = value;
    });
}

// -------------------------------------
// Init
// -------------------------------------

document.querySelectorAll(SIZE_GUIDE_SECTION)?.forEach((element, index) => {
    const horizontalTabs = element.querySelector(TAB_HORIZONTAL);
    const horizontalTabButtons = horizontalTabs?.querySelectorAll(TAB_BUTTON_HORIZONTAL);
    const horizontalTabContent = element.querySelectorAll(TAB_CONTENT_HORIZONTAL);
    const accordionTabs = element.querySelector(TAB_ACCORDION);

    if (horizontalTabs && horizontalTabButtons?.length && horizontalTabContent?.length) {
        // Set tabindex for all horizontal tab buttons
        updateAttributeForElements(horizontalTabButtons, 'tabindex', '-1');

        // Open the first horizontal tab by default
        openHorizontalTab(horizontalTabButtons[0], horizontalTabContent[0]);

        // Initialize horizontal tab buttons
        initHorizontalTabs(horizontalTabButtons, horizontalTabContent);
    }

    if (accordionTabs !== null) {
        const accordionTabContentItemLinks = element.querySelectorAll(TAB_CONTENT_ACCORDION + ' a');
        const accordionTabButtons = element.querySelectorAll(TAB_BUTTON_ACCORDION);

        if (accordionTabButtons?.length && accordionTabContentItemLinks?.length) {
            const tab = generateTab(accordionTabButtons[0]);

            // Set tabindex for all content links
            updateAttributeForElements(accordionTabContentItemLinks, 'tabindex', '-1');

            // Open the first tab by default
            openAccordionTab(tab);

            // Initialize accordion tabs
            initAccordionTabs(accordionTabButtons);
        }
    } else {
        const swiperContainer = element.querySelector(SWIPER_CLASS_SELECTOR);
        const swiperPrevButton = element.querySelector(SWIPER_NAV_PREV_CLASS_SELECTOR);
        const swiperNextButton = element.querySelector(SWIPER_NAV_NEXT_CLASS_SELECTOR);

        if (swiperContainer && swiperPrevButton && swiperNextButton) {
            const mobileBlockCount = swiperContainer.dataset.blockCount > 2 ? 2.1 : swiperContainer.dataset.blockCount;
            const blockCount = swiperContainer.dataset.blockCount;

            swiperContainer.classList.add(SWIPER_CLASS + '-' + index);
            swiperPrevButton.classList.add(SWIPER_NAV_PREV_CLASS + '-' + index);
            swiperNextButton.classList.add(SWIPER_NAV_NEXT_CLASS + '-' + index);

            new Swiper(SWIPER_CLASS_SELECTOR + '-' + index, {
                modules: [Navigation, A11y, Keyboard],
                slidesPerView: mobileBlockCount,
                spaceBetween: 0,
                autoHeight: true,
                observer: true,
                observeParents: true,
                a11y: false,
                watchOverflow: true,
                navigation: {
                    prevEl: SWIPER_NAV_PREV_CLASS_SELECTOR + '-' + index,
                    nextEl: SWIPER_NAV_NEXT_CLASS_SELECTOR + '-' + index
                },
                breakpoints: {
                    680: {
                        slidesPerView: blockCount
                    }
                }
            });
        }
    }
});
